import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useTheme } from '@flowcore-water/ui';
import { ActivityChart } from './ActivityChart';
import type { InstructorActivityEntry, User } from '../types';
import type { ThemeColors } from '@flowcore-water/ui';

/** Reusable multi-select dropdown with chip display */
const MultiSelect: React.FC<{
  label: string;
  options: { value: string; label: string }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  t: ThemeColors;
}> = ({ label, options, selected, onChange, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val: string) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(next);
  };

  const selectedLabels = options.filter((o) => selected.has(o.value)).map((o) => o.label);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="rounded-lg px-3 py-2 text-sm font-medium text-left min-w-[160px] flex items-center justify-between gap-2"
        style={{
          background: t.inputBg,
          border: `1px solid ${t.inputBorder}`,
          color: selected.size > 0 ? t.textPrimary : t.textMuted,
        }}
      >
        <span className="truncate">
          {selected.size === 0
            ? label
            : selected.size === 1
              ? selectedLabels[0]
              : `${selected.size} selected`}
        </span>
        <span style={{ color: t.textMuted }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 z-20 rounded-lg border shadow-xl max-h-64 overflow-auto min-w-[200px]"
          style={{ background: t.cardBg, borderColor: t.border }}
        >
          {selected.size > 0 && (
            <button
              onClick={() => onChange(new Set())}
              className="w-full text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide border-b"
              style={{ color: t.accent, borderColor: t.borderSubtle }}
            >
              Clear All
            </button>
          )}
          {options.map((o) => (
            <button
              key={o.value}
              onClick={() => toggle(o.value)}
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors"
              style={{ color: t.textPrimary }}
              onMouseEnter={(e) => (e.currentTarget.style.background = t.surfaceHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span
                className="w-4 h-4 rounded border flex items-center justify-center text-xs flex-shrink-0"
                style={{
                  borderColor: selected.has(o.value) ? t.accent : t.border,
                  background: selected.has(o.value) ? t.accentBg : 'transparent',
                  color: t.accent,
                }}
              >
                {selected.has(o.value) ? '✓' : ''}
              </span>
              <span className="truncate">{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** A group of consecutive entries sharing the same instructor+student */
interface EntryGroup {
  key: string;
  instructorName: string;
  studentName: string;
  entries: InstructorActivityEntry[];
}

/** Group consecutive entries by instructor+student pair */
function groupConsecutive(entries: InstructorActivityEntry[]): EntryGroup[] {
  const groups: EntryGroup[] = [];
  for (const e of entries) {
    const pairKey = `${e.instructorId}__${e.studentId}`;
    const last = groups[groups.length - 1];
    if (last && last.key === pairKey) {
      last.entries.push(e);
    } else {
      groups.push({ key: pairKey, instructorName: e.instructorName, studentName: e.studentName, entries: [e] });
    }
  }
  return groups;
}

const CATEGORY_COLORS = (t: ThemeColors): Record<string, { bg: string; text: string; border: string }> => ({
  discussion: { bg: 'rgba(34, 211, 238, 0.1)', text: t.accent, border: `1px solid ${t.accentDim}` },
  demonstration: { bg: 'rgba(168, 85, 247, 0.1)', text: '#c084fc', border: '1px solid rgba(168, 85, 247, 0.4)' },
  ridealong: { bg: 'rgba(251, 191, 36, 0.1)', text: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.4)' },
});

/** Single entry row for the desktop table */
const EntryRow: React.FC<{ e: InstructorActivityEntry; t: ThemeColors; catColors: ReturnType<typeof CATEGORY_COLORS> }> = ({ e, t, catColors }) => {
  const cat = catColors[e.category] || catColors.discussion;
  return (
    <tr
      className="border-b /40 hover:bg-white/5 transition-colors"
      style={{ background: t.cardBg }}
    >
      <td className="px-6 py-4 whitespace-nowrap" style={{ color: t.textSecondary }}>
        {e.completedAt.toLocaleDateString()}
      </td>
      <td className="px-6 py-4" style={{ color: t.textPrimary }}>{e.instructorName}</td>
      <td className="px-6 py-4" style={{ color: t.textPrimary }}>{e.studentName}</td>
      <td className="px-6 py-4" style={{ color: t.textSecondary }}>{e.qualificationName}</td>
      <td className="px-6 py-4" style={{ color: t.textPrimary }}>{e.itemTitle}</td>
      <td className="px-6 py-4">
        <span
          className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase"
          style={{ background: cat.bg, color: cat.text, border: cat.border }}
        >
          {e.category}
        </span>
      </td>
      <td className="px-6 py-4">
        <span
          className="inline-block rounded-full px-2.5 py-0.5 text-xs font-bold uppercase"
          style={{
            background: e.passed ? t.successBg : t.failBg,
            color: e.passed ? t.success : t.fail,
            border: `1px solid ${e.passed ? t.successBorder : t.failBorder}`,
          }}
        >
          {e.passed ? 'Pass' : 'Fail'}
        </span>
      </td>
    </tr>
  );
};

/** Single entry card for mobile */
const EntryCard: React.FC<{ e: InstructorActivityEntry; t: ThemeColors; catColors: ReturnType<typeof CATEGORY_COLORS> }> = ({ e, t, catColors }) => {
  const cat = catColors[e.category] || catColors.discussion;
  return (
    <div
      className="rounded-lg border p-4 space-y-2"
      style={{ background: t.cardBg, borderColor: t.border }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs" style={{ color: t.textMuted }}>{e.completedAt.toLocaleDateString()}</span>
        <div className="flex items-center gap-2">
          <span className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold uppercase" style={{ background: cat.bg, color: cat.text, border: cat.border }}>
            {e.category}
          </span>
          <span
            className="inline-block rounded-full px-2 py-0.5 text-xs font-bold uppercase"
            style={{
              background: e.passed ? t.successBg : t.failBg,
              color: e.passed ? t.success : t.fail,
              border: `1px solid ${e.passed ? t.successBorder : t.failBorder}`,
            }}
          >
            {e.passed ? 'Pass' : 'Fail'}
          </span>
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold" style={{ color: t.textPrimary }}>{e.itemTitle}</p>
        <p className="text-xs" style={{ color: t.textSecondary }}>{e.qualificationName}</p>
      </div>
      <div className="flex items-center justify-between text-xs" style={{ color: t.textMuted }}>
        <span>Instructor: <span style={{ color: t.textSecondary }}>{e.instructorName}</span></span>
        <span>Student: <span style={{ color: t.textSecondary }}>{e.studentName}</span></span>
      </div>
    </div>
  );
};

/** Collapsed group row for desktop — clickable to expand */
const CollapsedGroupRow: React.FC<{
  group: EntryGroup;
  expanded: boolean;
  onToggle: () => void;
  t: ThemeColors;
  catColors: ReturnType<typeof CATEGORY_COLORS>;
}> = ({ group, expanded, onToggle, t, catColors }) => {
  // If only 1 entry, just render it directly (no point collapsing)
  if (group.entries.length === 1) {
    return <EntryRow e={group.entries[0]} t={t} catColors={catColors} />;
  }

  const dateLabel = group.entries[0].completedAt.toLocaleDateString();

  return (
    <>
      <tr
        className="cursor-pointer border-b /40 hover:bg-white/5 transition-colors"
        style={{ background: t.cardBg }}
        onClick={onToggle}
      >
        <td className="px-6 py-4 whitespace-nowrap" style={{ color: t.textSecondary }}>{dateLabel}</td>
        <td className="px-6 py-4" style={{ color: t.textPrimary }}>{group.instructorName}</td>
        <td className="px-6 py-4" style={{ color: t.textPrimary }}>{group.studentName}</td>
        <td className="px-6 py-4" style={{ color: t.accent }}>
          <span className="text-xs font-semibold">
            {expanded ? '▼' : '▶'} {group.entries.length} signoffs
          </span>
        </td>
        <td className="px-6 py-4" />
        <td className="px-6 py-4" />
        <td className="px-6 py-4">
          {group.entries.every((e) => e.passed) ? (
            <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-bold uppercase"
              style={{ background: t.successBg, color: t.success, border: `1px solid ${t.successBorder}` }}>
              All Pass
            </span>
          ) : (
            <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-bold uppercase"
              style={{ background: t.failBg, color: t.fail, border: `1px solid ${t.failBorder}` }}>
              Mixed
            </span>
          )}
        </td>
      </tr>
      {expanded && group.entries.map((e) => (
        <EntryRow key={e.id} e={e} t={t} catColors={catColors} />
      ))}
    </>
  );
};

/** Collapsed group card for mobile */
const CollapsedGroupCard: React.FC<{
  group: EntryGroup;
  expanded: boolean;
  onToggle: () => void;
  t: ThemeColors;
  catColors: ReturnType<typeof CATEGORY_COLORS>;
}> = ({ group, expanded, onToggle, t, catColors }) => {
  if (group.entries.length === 1) {
    return <EntryCard e={group.entries[0]} t={t} catColors={catColors} />;
  }

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full rounded-lg border p-4 text-left"
        style={{ background: t.cardBg, borderColor: t.border }}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: t.textMuted }}>
            {group.entries[0].completedAt.toLocaleDateString()}
          </span>
          {group.entries.every((e) => e.passed) ? (
            <span className="inline-block rounded-full px-2 py-0.5 text-xs font-bold uppercase"
              style={{ background: t.successBg, color: t.success, border: `1px solid ${t.successBorder}` }}>
              All Pass
            </span>
          ) : (
            <span className="inline-block rounded-full px-2 py-0.5 text-xs font-bold uppercase"
              style={{ background: t.failBg, color: t.fail, border: `1px solid ${t.failBorder}` }}>
              Mixed
            </span>
          )}
        </div>
        <p className="text-sm font-semibold mt-1" style={{ color: t.textPrimary }}>
          <span style={{ color: t.accent }}>{expanded ? '▼' : '▶'}</span>{' '}
          {group.instructorName} signed off {group.entries.length} things for {group.studentName}
        </p>
      </button>
      {expanded && (
        <div className="ml-4 mt-2 space-y-2">
          {group.entries.map((e) => (
            <EntryCard key={e.id} e={e} t={t} catColors={catColors} />
          ))}
        </div>
      )}
    </div>
  );
};

/** Column widths for stable table layout */
const COL_WIDTHS = ['11%', '14%', '14%', '16%', '22%', '12%', '11%'];
const TABLE_COLS = ['Date', 'Instructor', 'Student', 'Qualification', 'Item', 'Category', 'Result'];

const ActivityTableHead: React.FC<{ t: ThemeColors }> = ({ t }) => (
  <>
    <colgroup>
      {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
    </colgroup>
    <thead>
      <tr className="border-b /60" style={{ background: '#0a0f1a' }}>
        {TABLE_COLS.map((h) => (
          <th key={h} className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wide"
            style={{ color: t.textSecondary }}>
            {h}
          </th>
        ))}
      </tr>
    </thead>
  </>
);

export const InstructorActivity: React.FC<{ users: User[] }> = () => {
  const { getInstructorActivity } = useData(undefined);
  const { t } = useTheme();

  const [allEntries, setAllEntries] = useState<InstructorActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstructors, setSelectedInstructors] = useState<Set<string>>(new Set());
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getInstructorActivity().then((data) => {
      if (!cancelled) {
        setAllEntries(data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [getInstructorActivity]);

  const instructorOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of allEntries) {
      if (!seen.has(e.instructorId)) seen.set(e.instructorId, e.instructorName);
    }
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [allEntries]);

  const studentOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of allEntries) {
      if (!seen.has(e.studentId)) seen.set(e.studentId, e.studentName);
    }
    return Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [allEntries]);

  const entries = useMemo(() => {
    return allEntries.filter((e) => {
      if (selectedInstructors.size > 0 && !selectedInstructors.has(e.instructorId)) return false;
      if (selectedStudents.size > 0 && !selectedStudents.has(e.studentId)) return false;
      return true;
    });
  }, [allEntries, selectedInstructors, selectedStudents]);

  const groups = useMemo(() => groupConsecutive(entries), [entries]);

  // Reset expanded groups when filters or collapse toggle change
  useEffect(() => { setExpandedGroups(new Set()); }, [collapsed, selectedInstructors, selectedStudents]);

  const toggleGroup = (idx: number) => {
    const next = new Set(expandedGroups);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setExpandedGroups(next);
  };

  const catColors = useMemo(() => CATEGORY_COLORS(t), [t]);
  const hasFilters = selectedInstructors.size > 0 || selectedStudents.size > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: t.textPrimary }}>
            Activity
          </h1>
          <p className="mt-1 text-sm" style={{ color: t.textSecondary }}>
            All signoffs across students
          </p>
        </div>

        {/* Filters + collapse toggle */}
        <div className="flex flex-wrap items-center gap-3">
          <MultiSelect label="All Instructors" options={instructorOptions} selected={selectedInstructors} onChange={setSelectedInstructors} t={t} />
          <MultiSelect label="All Students" options={studentOptions} selected={selectedStudents} onChange={setSelectedStudents} t={t} />
          {hasFilters && (
            <button
              onClick={() => { setSelectedInstructors(new Set()); setSelectedStudents(new Set()); }}
              className="text-xs font-semibold uppercase tracking-wide px-2 py-1"
              style={{ color: t.accent }}
            >
              Clear Filters
            </button>
          )}

          {/* Collapse toggle — push to far right */}
          <div className="ml-auto">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="rounded-lg px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors"
              style={{
                background: collapsed ? t.accentBg : 'transparent',
                border: `1px solid ${collapsed ? t.accentDim : t.border}`,
                color: collapsed ? t.accent : t.textMuted,
              }}
            >
              {collapsed ? 'Grouped' : 'Expanded'}
            </button>
          </div>
        </div>
      </div>

      {entries.length > 0 && <ActivityChart activity={entries} t={t} />}

      {loading ? (
        <div className="text-center py-12" style={{ color: t.textMuted }}>Loading activity...</div>
      ) : entries.length === 0 ? (
        <div
          className="rounded-lg border p-8 text-center"
          style={{ background: t.cardBg, borderColor: t.border, color: t.textMuted }}
        >
          {allEntries.length === 0 ? 'No instructor activity found.' : 'No activity matches the selected filters.'}
        </div>
      ) : collapsed ? (
        <>
          {/* GROUPED VIEW — Desktop */}
          <div className="hidden md:block rounded-lg border /60 overflow-hidden" style={{ background: '#0d1321' }}>
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <ActivityTableHead t={t} />
              <tbody>
                {groups.map((g, i) => (
                  <CollapsedGroupRow
                    key={`${g.key}_${i}`}
                    group={g}
                    expanded={expandedGroups.has(i)}
                    onToggle={() => toggleGroup(i)}
                    t={t}
                    catColors={catColors}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* GROUPED VIEW — Mobile */}
          <div className="md:hidden space-y-3">
            {groups.map((g, i) => (
              <CollapsedGroupCard
                key={`${g.key}_${i}`}
                group={g}
                expanded={expandedGroups.has(i)}
                onToggle={() => toggleGroup(i)}
                t={t}
                catColors={catColors}
              />
            ))}
          </div>

          <p className="text-xs text-right" style={{ color: t.textMuted }}>
            {groups.length} group{groups.length !== 1 ? 's' : ''} · {entries.length}{hasFilters ? ` of ${allEntries.length}` : ''} signoff{entries.length !== 1 ? 's' : ''}
          </p>
        </>
      ) : (
        <>
          {/* FLAT VIEW — Desktop */}
          <div className="hidden md:block rounded-lg border /60 overflow-hidden" style={{ background: '#0d1321' }}>
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <ActivityTableHead t={t} />
              <tbody>
                {entries.map((e) => <EntryRow key={e.id} e={e} t={t} catColors={catColors} />)}
              </tbody>
            </table>
          </div>

          {/* FLAT VIEW — Mobile */}
          <div className="md:hidden space-y-3">
            {entries.map((e) => <EntryCard key={e.id} e={e} t={t} catColors={catColors} />)}
          </div>

          <p className="text-xs text-right" style={{ color: t.textMuted }}>
            {entries.length}{hasFilters ? ` of ${allEntries.length}` : ''} signoff{entries.length !== 1 ? 's' : ''}
          </p>
        </>
      )}
    </div>
  );
};
